import { useEffect, useRef, type ReactNode } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cn } from "@/lib/utils";

gsap.registerPlugin(ScrollTrigger);

interface HeroParallaxBandProps {
  children: ReactNode;
  className?: string;
}

/** Hero photo with strong left scrim so editorial type stays readable */
export function HeroParallaxBand({ children, className }: HeroParallaxBandProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current || !mediaRef.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.to(mediaRef.current, {
        yPercent: 18,
        scale: 1.08,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1.2,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative overflow-hidden border-b border-border min-h-[calc(100vh-4.5rem)] flex items-end",
        className
      )}
    >
      <div ref={mediaRef} className="absolute inset-0 -z-20 will-change-transform">
        <img
          src="/christian-wiediger-PSmDDeXaEWE-unsplash.jpg"
          alt=""
          className="h-[120%] w-full object-cover object-[center_30%]"
          aria-hidden
        />
      </div>
      <div
        className="absolute inset-0 -z-10 bg-gradient-to-r from-background via-background/90 to-background/55"
        aria-hidden
      />
      <div
        className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-background/75 to-transparent"
        aria-hidden
      />

      {children}
    </section>
  );
}
